<template>
    <div
        id="bpmn"
        :class="{
            'disabled': props.isDisabled,
            'editing': isEditing,
            'navigatable': props.isNavigationEnabled,
        }"
    >
        <div v-if="props.isDisabled" id="disabled-overlay" />
        <div id="diagram-container" ref="bpmnJsElement"></div>
        <div class="floating" v-if="props.isMaximizingEnabled">
            <Button
                v-if="organization && stek && process"
                severity="secondary"
                class="maximize-button"
                icon="pi pi-arrow-up-right-and-arrow-down-left-from-center"
                label="Maximize"
                size="small"
                as="RouterLink"
                :to="{
                    name: 'processDefinitionPage',
                    params: {
                        orgName: organization.name,
                        stekName: stek.name,
                        processId: process.identity,
                    },
                }"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, watch, ref, computed, readonly } from "vue";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import {
    ImportBPMNDiagramError,
    ImportWarning,
    useBpmnManager,
} from "@/composables/bpmn-manager/index";
import { useEditManager } from "@/composables/edit-manager";
import { useBpmnLayerManager } from "@/composables/bpmn-manager/layer";
import { debounce } from "@/utils";
import { BpmnArtefact } from "@/lib/artefacts";
import { useProcessElementsManager } from "@/stores/process-elements-store";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useProcessManager } from "@/composables/process-manager";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import { useToast } from "primevue/usetoast";
import { type ToastMessageOptions } from "primevue/toast";
import {
    elementIsEvent,
    elementIsMessageFlow,
    elementIsGateway,
    elementIsTask,
} from "@/composables/bpmn-manager/helpers";
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const { process } = useProcessManager();

// To allow using multiple root nodes without warnings, we don't need
// fallthrough props here anyway.
defineOptions({ inheritAttrs: false });

const bpmnArtefact = defineModel<BpmnArtefact>("bpmnArtefact", {
    required: true,
});

const highlightElementId = defineModel<string | null>("highlightElementId", {
    default: "",
});

const props = defineProps({
    isDisabled: {
        type: Boolean,
        default: false,
    },
    isMaximizingEnabled: {
        type: Boolean,
        default: true,
    },
    isNavigationEnabled: {
        type: Boolean,
        default: true,
    },
    isScrollingEnabled: {
        type: Boolean,
        default: true,
    },
    isZoomOnHoverEnabled: {
        type: Boolean,
        default: false,
    },
});

const route = useRoute();
const router = useRouter();
const toast = useToast();
const bpmnJsElement = ref<HTMLElement>();
const bpmnManager = useBpmnManager();

defineExpose({
    getSvg: bpmnManager.getSvg,
    completeActiveActions: bpmnManager.completeActiveActions,
});

const { isEditing } = useEditManager();
const roleSelect = true;
const layerManager = useBpmnLayerManager(bpmnManager, {
    highlight: {
        enabled: readonly(ref(false)),
    },
    dotForMissingTasks: {
        enabled: readonly(ref(false)),
    },
    navigationalElements: {
        enabled: computed(() => !isEditing.value),
        callbacks: {
            onHoveringNavigationalElement: (elementId) => {
                highlightElementId.value = elementId;
            },
        },
    },
    taskWindowElements: {
        enabled: computed(() => isEditing.value),
    },
    roleSelect: {
        enabled: readonly(ref(roleSelect)),
    },
    callActivityMapper: {
        enabled: readonly(ref(true)),
    },
});

watch(highlightElementId, (elementId, oldElementId) => {
    if (oldElementId) {
        layerManager.clearHighlight(oldElementId);
    }

    if (elementId) {
        layerManager.highlight(elementId);
    }
});

const { processElements } = useProcessElementsManager();
watch(bpmnManager.selectedBpmnElement, (element) => {
    if (!isEditing.value) {
        if (!element) {
            return;
        }

        let processElementId = getBusinessObject(element).processElementId;
        if (!processElementId) {
            const processElement = processElements.value?.find(
                (task) => element.businessObject?.id === task.bpmn_id
            );
            processElementId = processElement?.identity;
        }

        if (processElementId) {
            if (elementIsTask(element)) {
                router.push({
                    name: "taskPage",
                    params: { ...route.params, taskId: processElementId },
                });
            } else if (elementIsGateway(element)) {
                router.push({
                    name: "gatewayPage",
                    params: { ...route.params, gatewayId: processElementId },
                });
            } else if (elementIsMessageFlow(element)) {
                router.push({
                    name: "flowPage",
                    params: { ...route.params, flowId: processElementId },
                });
            } else if (elementIsEvent(element)) {
                router.push({
                    name: "eventPage",
                    params: { ...route.params, eventId: processElementId },
                });
            }
        }
    }
});

const debounceCenter = debounce(() => bpmnManager.resetZoom(), 100);

let resizeObserver: ResizeObserver;
onMounted(() => {
    initBpmnJs();
    resizeObserver = new ResizeObserver(debounceCenter);
    resizeObserver.observe(bpmnJsElement.value!);
});

onUnmounted(() => {
    bpmnManager.destroy();
    resizeObserver?.disconnect();
});

// Adding the toast secretly adds an id value to it, we need that to remove it.
const errorMessageToast = ref<ToastMessageOptions & { id?: number }>();
const warningsMessageToast = ref<ToastMessageOptions & { id?: number }>();

/**
 * Extract the message from an ImportWarning object.
 */
const messageFromWarning = (warning: ImportWarning) => {
    if (Object.hasOwn(warning, "message")) {
        return (warning as ImportWarning).message;
    }
    return warning;
};

/**
 * Show a toast with an error message if the BPMN is invalid.
 * Remove an existing toast for any prior BPMN errors.
 */
const showErrorToast = (error: ImportBPMNDiagramError) => {
    if (errorMessageToast.value?.id !== undefined) {
        toast.remove(errorMessageToast.value);
    }
    errorMessageToast.value = {
        severity: "error",
        summary: "An invalid BPMN was loaded.",
        detail: error.warnings.map(messageFromWarning).join(","),
    };
    toast.add(errorMessageToast.value);
};

/**
 * Show a toast with a warning message if the BPMN has warnings.
 * Remove existing toast for the same warnings.
 */
const showWarningToast = (warnings: ImportWarning[]) => {
    if (warningsMessageToast.value?.id !== undefined) {
        toast.remove(warningsMessageToast.value);
    }
    warningsMessageToast.value = {
        severity: "info",
        summary: "Some issues were found in the BPMN.",
        detail: warnings.map(messageFromWarning).join(","),
    };
    toast.add(warningsMessageToast.value);
};

/**
 * Initialize the bpmn-js component, either as editor or as viewer
 */
const initBpmnJs = async () => {
    let viewbox = bpmnManager.getViewbox() ?? null;
    if (!viewbox) {
        try {
            viewbox = JSON.parse(
                sessionStorage.getItem(
                    `${bpmnArtefact.value.identity}-viewbox`
                ) ?? "undefined"
            );
            // Make sure it's not hanging around for when you get back later.
            sessionStorage.removeItem(`${bpmnArtefact.value.identity}-viewbox`);
        } catch {
            // no or invalid value, leave as is.
        }
    }
    const rootElement = bpmnManager.getRootElement();
    let rootElementId: string | undefined | null;
    if (rootElement) {
        rootElementId = rootElement.id;
    } else {
        rootElementId = sessionStorage.getItem(
            `${bpmnArtefact.value.identity}-rootElementId`
        );
        // Make sure it's not hanging around for when you get back later.
        sessionStorage.removeItem(
            `${bpmnArtefact.value.identity}-rootElementId`
        );
    }

    if (isEditing.value && !props.isDisabled) {
        bpmnManager.initModeler(bpmnJsElement.value!, undefined, {
            hideLaneNames: roleSelect,
        });
    } else {
        bpmnManager.initViewer(bpmnJsElement.value!, {
            isNavigationEnabled: props.isNavigationEnabled,
            isScrollingEnabled: props.isScrollingEnabled,
            isZoomOnHoverEnabled: props.isZoomOnHoverEnabled,
            hideLaneNames: roleSelect,
        });
    }

    layerManager.attach();

    try {
        const { warnings } = await bpmnManager.loadBpmn(
            bpmnArtefact.value.content
        );
        if (warnings?.length) {
            showWarningToast(warnings);
        }
    } catch (e) {
        showErrorToast(e as ImportBPMNDiagramError);
        bpmnArtefact.value.resetContent();
        return;
    }
    if (viewbox && (props.isNavigationEnabled || isEditing.value)) {
        bpmnManager.setViewbox(viewbox);
    } else {
        bpmnManager.resetZoom();
    }
    if (rootElementId) {
        bpmnManager.setRootElement(rootElementId);
    }
};

onBeforeRouteLeave(() => {
    const viewbox = bpmnManager.getViewbox();
    if (viewbox) {
        sessionStorage.setItem(
            `${bpmnArtefact.value.identity}-viewbox`,
            JSON.stringify(viewbox)
        );
    }
    const rootElement = bpmnManager.getRootElement();
    if (rootElement && rootElement.id) {
        sessionStorage.setItem(
            `${bpmnArtefact.value.identity}-rootElementId`,
            rootElement.id
        );
    }
});

watch(() => isEditing.value, initBpmnJs);

// Sometimes the artefact is changed from the outside (e.g. when the user
// discards their changes) - in that case we reload the bpmn.
watch(
    () => bpmnArtefact.value.content,
    async (newContent, oldContent) => {
        if (newContent && newContent !== bpmnManager.xml.value) {
            try {
                const { warnings } = await bpmnManager.loadBpmn(newContent);
                if (warnings?.length) {
                    showWarningToast(warnings);
                }
            } catch (e) {
                showErrorToast(e as ImportBPMNDiagramError);
                bpmnArtefact.value.content = oldContent;
                return;
            }
        }
    }
);

watch(bpmnManager.xml, (newValue) => {
    if (
        isEditing.value &&
        newValue &&
        newValue !== bpmnArtefact.value.content
    ) {
        bpmnArtefact.value.updateContent(newValue);
    }
});
</script>

<style lang="scss" scoped>
.floating {
    position: absolute;
    right: 1rem;
}

.maximize-button {
    text-decoration: none;
}

#bpmn {
    display: flex;
    flex-grow: 1;
    flex-basis: 300px;

    #diagram-container {
        flex: 1;

        :deep(.djs-container.djs-parent) svg {
            outline: none;
        }
    }

    :deep(.simulation) {
        .bjsl-button {
            display: none;
        }
    }

    /* Avoid showing the 'move' cursor when things are not movable */
    &:not(.navigatable):not(.editing) {
        :deep(.djs-element) {
            .djs-hit-all,
            .djs-hit-stroke,
            .djs-hit-click-stroke,
            .djs-connection {
                cursor: unset;
            }
        }
    }

    &.editing {
        :deep(.djs-element) {
            &.hover {
                .djs-outline {
                    stroke: var(--stekz-biz-lightest-color);
                }
            }
            &.selected {
                .djs-outline {
                    stroke: var(--stekz-biz-darkest-color);
                    stroke-width: 2px;
                }
                /* Selected task (rect), gateway (polygon) or start/end/error (circle) */
                .djs-visual {
                    rect,
                    circle,
                    polygon {
                        fill: var(--stekz-biz-lightest-color) !important;
                    }
                }
            }
        }
    }

    &:not(.editing) {
        :deep(.djs-element) {
            &.hover {
                .djs-outline {
                    stroke: unset;
                }
            }
            &.selected {
                .djs-outline {
                    stroke: unset;
                }
            }
            .djs-hit-stroke,
            .djs-hit-click-stroke,
            .djs-hit-all {
                cursor: unset;
            }
        }
    }

    :deep(.djs-element) {
        .djs-call-activity .djs-label,
        .djs-call-activity .djs-label ~ *, // The icon rect and plus sign
        .djs-closed-pool .djs-label,
        .djs-lane .djs-label {
            display: none;
        }
    }

    &.disabled {
        #disabled-overlay {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            background: var(--p-content-background);
            opacity: 0.9;
            z-index: 1;
        }
    }
}
</style>
<style lang="css">
@import url("bpmn-js/dist/assets/diagram-js.css");
@import url("bpmn-js/dist/assets/bpmn-js.css");
@import url("bpmn-js/dist/assets/bpmn-font/css/bpmn.css");
@import url("bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css");
@import url("bpmn-js-bpmnlint/dist/assets/css/bpmn-js-bpmnlint.css");
@import url("@bpmn-io/properties-panel/assets/properties-panel.css");
@import url("bpmn-js-token-simulation/assets/css/bpmn-js-token-simulation.css");
@import url("bpmn-js-color-picker/colors/color-picker.css");
/* Overwrite token-sinulation colors */
:root {
    --token-simulation-green-base-44: var(--stekz-primary-color);
}
</style>
